import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Skeleton } from "primereact/skeleton";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import ReactCountryFlag from "react-country-flag";
import { getName } from "country-list";
import PermissionGate from "~/components/PermissionGate";
import Menu from "~/components/menu/Menu";
import { currencyList } from "~/utils/currency";
import apiService from "~/services/service";
import API_ROUTES from "~/api-routes";

export interface OtherEntityTransaction {
  id: string;
  entity_id?: string;
  type?: string;
  acquisition_date?: string;
  number_of_shares?: number;
  share_value?: number;
  share_percentage?: number;
  share_class?: string;
  currency?: string;
  entityData?: {
    id?: string;
    name?: string;
    country?: string;
  };
}

interface OtherEntitiesShareRegistryTableProps {
  transactions: OtherEntityTransaction[];
  loading?: boolean;
  onEditTransaction?: (transaction: OtherEntityTransaction) => void;
  onDeleteTransaction?: (transaction: OtherEntityTransaction) => void;
  onPageChange?: (event: any) => void;
  page?: number;
  pageSize?: number;
  totalRecords?: number;
  entityId?: string;
}

const OtherEntitiesShareRegistryTable: React.FC<
  OtherEntitiesShareRegistryTableProps
> = ({
  transactions,
  loading = false,
  onEditTransaction,
  onDeleteTransaction,
  onPageChange,
  page = 0,
  pageSize = 10,
  totalRecords = 0,
  entityId,
}) => {
  const skeletonRows = [...Array(5)];

  // Get user's admin status from Redux store
  const { isAdmin } = useSelector((state: any) => state.login);

  // State to store user's accessible entity IDs
  const [accessibleEntityIds, setAccessibleEntityIds] = useState<string[]>([]);

  // Fetch user's accessible entities on component mount
  useEffect(() => {
    const fetchUserEntities = async () => {
      try {
        const response: any = await apiService(API_ROUTES.ALL_ENTITIES, "GET");
        const entities = response?.data || [];
        // Extract entity IDs (check both id and original_pk fields)
        const entityIds = entities.map(
          (entity: any) => entity.id || entity.original_pk,
        );
        setAccessibleEntityIds(entityIds);
        console.log("[OtherEntities] Fetched accessible entities:", entityIds);
      } catch (error) {
        console.error("[OtherEntities] Error fetching entities:", error);
        setAccessibleEntityIds([]);
      }
    };

    if (!isAdmin) {
      fetchUserEntities();
    }
  }, [isAdmin]);

  const handlePageChange = (event: any) => {
    onPageChange?.({ page: event.page, rows: event.rows });
  };

  // Check if user has access to a specific entity
  const hasEntityAccess = (
    entityId: string | undefined,
    entityDataId: string | undefined,
  ): boolean => {
    if (isAdmin) return true;
    if (!entityId && !entityDataId) return false;

    // Check if user has access to either ID
    const hasAccess =
      (entityId ? accessibleEntityIds.includes(entityId) : false) ||
      (entityDataId ? accessibleEntityIds.includes(entityDataId) : false);

    return hasAccess;
  };

  const entityTemplate = (rowData: OtherEntityTransaction) => {
    if (loading) return <Skeleton width="100%" />;
    const countryName = rowData?.entityData?.country
      ? getName(rowData.entityData.country) || rowData.entityData.country
      : null;

    const linkStyle = {
      color: "var(--button-link-color, #0B4720)",
      fontFamily: "Inter",
      fontSize: "16px",
      fontStyle: "normal",
      fontWeight: "var(--fontweight-regular, 400)" as const,
      textDecoration: "underline",
    };

    const content = (
      <>
        {rowData?.entityData?.name || "-"}
        {rowData?.entityData?.country && (
          <>
            {" ("}
            <ReactCountryFlag
              countryCode={rowData.entityData.country}
              svg
              style={{ width: "1em", height: "1em", marginRight: "0.25rem" }}
            />
            {countryName})
          </>
        )}
      </>
    );

    // Check access using both IDs (API returns different entity_id and entityData.id)
    const canAccessEntity = hasEntityAccess(
      rowData?.entity_id,
      rowData?.entityData?.id,
    );
    // Use entityData.id for navigation as it represents the actual entity
    const navigationEntityId = rowData?.entityData?.id || rowData?.entity_id;

    return navigationEntityId && canAccessEntity ? (
      <Link to={`/entity/${navigationEntityId}/dashboard`} style={linkStyle}>
        {content}
      </Link>
    ) : (
      <span>{content}</span>
    );
  };

  const shareValueTemplate = (rowData: OtherEntityTransaction) => {
    if (loading) return <Skeleton width="100%" />;
    const currencyCode = rowData?.currency;
    const currency = currencyList.find((c) => c.value === currencyCode);
    const symbol = currency?.symbol;
    return (
      <span>
        {symbol} {rowData?.share_value}
      </span>
    );
  };

  const actionMenuItems = (rowData: OtherEntityTransaction) => [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: () => {
        onEditTransaction?.(rowData);
      },
    },
    {
      label: "Delete Transaction",
      icon: "pi pi-trash",
      command: () => {
        onDeleteTransaction?.(rowData);
      },
    },
  ];

  return (
    <div className="table-container">
      <DataTable
        value={loading ? skeletonRows : transactions}
        rowGroupMode="rowspan"
        lazy
        paginator={totalRecords > 10}
        first={page * pageSize}
        rows={pageSize}
        totalRecords={totalRecords}
        rowsPerPageOptions={[10, 20, 30]}
        onPage={handlePageChange}
        className="datatable-right-align"
        emptyMessage="No results were found"
      >
        <Column
          field="entity_name"
          header="Entity"
          style={{ minWidth: "200px" }}
          body={entityTemplate}
        />
        <Column
          header="Type"
          style={{ minWidth: "100px" }}
          body={(rowData) =>
            loading ? <Skeleton width="100%" /> : rowData?.type || "-"
          }
        />
        <Column
          header="Transaction Date"
          style={{ minWidth: "150px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : rowData?.acquisition_date ? (
              formatDate(rowData.acquisition_date)
            ) : (
              "-"
            )
          }
        />
        <Column
          header="# Of Shares"
          style={{ minWidth: "100px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <span>{rowData?.number_of_shares}</span>
            )
          }
        />
        <Column
          header="Share Value"
          style={{ minWidth: "120px" }}
          body={shareValueTemplate}
        />
        <Column
          header="Share %"
          style={{ minWidth: "80px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <span>{rowData?.share_percentage}</span>
            )
          }
        />
        <Column
          header="Share Class"
          style={{ minWidth: "100px" }}
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <span>{rowData?.share_class}</span>
            )
          }
        />
        <Column
          className="table-col-width"
          body={(rowData) =>
            loading ? (
              <Skeleton width="100%" />
            ) : (
              <PermissionGate
                resource="shareholder"
                action="modify"
                resourceAttr={{ entity_id: entityId }}
              >
                <div className="flex align-items-center justify-content-end gap-0">
                  <Menu menuItems={actionMenuItems(rowData)} />
                </div>
              </PermissionGate>
            )
          }
        />
      </DataTable>
    </div>
  );
};

export default OtherEntitiesShareRegistryTable;
